import type { DownloadStatus, DownloadTaskItem } from '@/services/downloadTasks';

/** 下载任务状态文案，列表和文件明细沿用同一套语言。 */
export const statusLabels: Record<DownloadStatus, string> = {
  PENDING: '排队中', RUNNING: '处理中', SUCCESS: '全部成功',
  PARTIAL_SUCCESS: '部分成功', FAILED: '失败', EXPIRED: '文件已清理',
};

/** 每条冻结明细对应的展示行；只解析路径展示，不拼接下载地址。 */
export interface DownloadFileRow {
  /** 原始文件结果，ID全程保留字符串。 */
  item: DownloadTaskItem;
  /** 文件名或没有产出文件的类型名称。 */
  name: string;
  /** 报告名称目录；旧任务没有该目录时显示任务ID。 */
  taskName: string;
  /** 任务内的公共、逐套和类型目录；缺失文件集中在虚拟的未打包项目下。 */
  directories: string[];
  /** 所属目录及其父节点键，用于同时选中整个任务或子目录。 */
  ancestorKeys: string[];
  /** 文件、目录、类型、业务ID及失败原因的搜索文本，不参与日志。 */
  searchText: string;
}

/** 只包含目录的导航节点；文件本身在右侧列表展示，避免重复长路径。 */
export interface DownloadDirectory {
  /** 使用字符串ID和目录段构造的稳定节点键。 */
  key: string;
  /** 单一目录段或任务报告名称。 */
  name: string;
  /** 业务任务ID；全部任务节点为空。 */
  taskId?: string;
  /** 任务内相对目录段，任务和总览节点为空。 */
  path: string[];
  /** 当前目录及其后代的明细数量，包含未打包项。 */
  count: number;
  /** 当前目录及其后代的失败数量。 */
  failures: number;
  /** 子目录节点，保留ZIP内原有目录层级。 */
  children: DownloadDirectory[];
}

/** @param status 后台状态 @returns 下载中心统一的Ant Design状态颜色 */
export function downloadStatusColor(status: DownloadStatus): string {
  return status === 'SUCCESS' ? 'green' : status === 'FAILED' ? 'red'
    : status === 'PARTIAL_SUCCESS' ? 'orange' : isPendingStatus(status) ? 'blue' : 'default';
}

/** @param status 下载任务状态 @returns 排队和处理中的状态需要强调当前进度 */
function isPendingStatus(status: DownloadStatus): boolean { return status === 'PENDING' || status === 'RUNNING'; }

/** @param bytes 字节数，允许后端Long字符串 @returns 小文件不显示为0.00MiB，列表与明细共用单位 */
export function downloadFileSize(bytes?: number | string): string {
  if (bytes == null) return '—';
  const value = Number(bytes);
  if (!Number.isFinite(value) || value < 0) return '—';
  if (value < 1024) return `${value} B`;
  return value < 1024 * 1024 ? `${(value / 1024).toFixed(1)} KiB` : `${(value / 1024 / 1024).toFixed(2)} MiB`;
}

/** @param taskId 业务ID @param path 任务内目录 @returns 无分隔符歧义且无数字精度损失的目录身份 */
function directoryKey(taskId: string, path: string[]): string { return JSON.stringify([taskId, ...path]); }

/** @param item 冻结项 @returns 原始路径段；旧版三格式平铺目录保留为任务内分类 */
function pathParts(item: DownloadTaskItem): { parts: string[]; legacy: boolean } {
  const parts = (item.fileName || '').split('/').filter(Boolean);
  return { parts, legacy: parts.length === 2 && ['SIGNED_PDF', 'UNSIGNED_PDF', 'WORD'].includes(parts[0]) };
}

/**
 * 根据完整冻结清单构造目录导航和文件行；缺失文件不会丢失，历史目录不误当作报告名称。
 * @param items 本次任务完整明细，不能只传一个接口分页 @param labels 服务端类型名称
 * @returns 根目录、按键查找目录及右侧文件行；不改变原始明细顺序或状态
 */
export function buildDownloadFileTree(items: DownloadTaskItem[], labels: Record<string, string>) {
  const taskNames = new Map<string, string>();
  for (const item of items) {
    const { parts, legacy } = pathParts(item);
    if (parts.length > 1 && !legacy && !taskNames.has(item.taskId)) taskNames.set(item.taskId, parts[0]);
  }
  const root: DownloadDirectory = { key: 'all', name: '全部任务', path: [], count: 0, failures: 0, children: [] };
  const nodes = new Map<string, DownloadDirectory>([['all', root]]);
  const rows: DownloadFileRow[] = [];
  for (const item of items) {
    const { parts, legacy } = pathParts(item);
    const taskName = taskNames.get(item.taskId) || `任务 ${item.taskId}`;
    const name = parts.length ? parts[parts.length - 1] : labels[item.format] || item.format;
    const directories = !parts.length ? ['未打包项目'] : parts.slice(legacy ? 0 : 1, -1);
    const ancestorKeys = ['all'];
    let parent = root;
    const segments = [taskName, ...directories];
    for (let index = 0; index < segments.length; index++) {
      const path = directories.slice(0, index);
      const key = directoryKey(item.taskId, path);
      let node = nodes.get(key);
      if (!node) {
        node = { key, name: segments[index], taskId: item.taskId, path, count: 0, failures: 0, children: [] };
        nodes.set(key, node); parent.children.push(node);
      }
      ancestorKeys.push(key); parent = node;
    }
    for (const key of ancestorKeys) {
      const node = nodes.get(key)!;
      node.count++; if (item.status === 'FAILED') node.failures++;
    }
    rows.push({ item, name, taskName, directories, ancestorKeys,
      searchText: [name, taskName, item.taskId, directories.join('/'), labels[item.format] || item.format,
        item.errorMessage || ''].join(' ').toLocaleLowerCase() });
  }
  return { root, nodes, rows };
}
